import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Typography,
  Radio,
  Input,
  Button,
  Space,
  Spin,
  Modal,
  Result,
  Divider,
  message
} from 'antd';
import {
  ClockCircleOutlined,
  FlagOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { QuizTopBar, QuizProgressStrip, QuestionPalette, QuizNavBar, QuizBackButton, QuizPrimaryButton } from './quiz/QuizAtoms';
import { ExtractsBlock, renderQuestionInput, isTextInputQuestion } from './quiz/QuizTypeRenderers';
import { useCountdown } from './quiz/useCountdown';
import axios from 'axios';
import PaywallUpsellCard from './billing/PaywallUpsellCard';
import { getPaywallError, PaywallError } from '../api/billingErrors';

const { Title, Text } = Typography;

// RichTextContent may arrive as a plain string or the typed { text } object.
type RichTextLike = string | { text?: string } | null | undefined;

// The typed reading-comprehension passage shape (shared/src/types/questions.ts
// -> ReadingComprehensionContent.passage). `content` is RichTextContent, but
// harvested data may also deliver it as a plain string, so we accept both.
interface PassageShape {
  title?: string;
  content?: RichTextLike;
  wordCount?: number;
  readingLevel?: string;
  source?: string;
  genre?: string;
  author?: string; // legacy ReadingTest passages carry an author
}

interface Question {
  id: string;
  type: string;
  title: string;
  subject?: string;
  source_metadata?: {
    naplan?: { requiresManualRubric?: boolean; yearLevel?: number; sourceYear?: number };
  };
  content: {
    pageNumber?: number;
    questionText?: string;
    question?: string;
    fullPromptText?: string;
    responseFormat?: string; // 'essay' for writing prompts
    hasRichContent?: boolean;
    sourceUploadId?: string;
    // Reading-passage shapes we know how to render (render-only; see report).
    passage?: PassageShape;
    passageId?: string; // groups consecutive questions sharing one passage
    stimulus?: RichTextLike; // flat fallback used by some extractors
  };
  answers: {
    text: string;
    value: string;
  }[];
  difficulty: string;
}

// Normalise any RichTextContent-like value down to a plain string.
const richTextToString = (value: RichTextLike): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.text || '';
};

// Extract a renderable passage from a question, if one is present.
// Detection order: typed `content.passage` object -> flat `content.stimulus`.
const getPassage = (question?: Question): PassageShape | null => {
  if (!question) return null;
  const c = question.content;
  if (c?.passage && (c.passage.title || richTextToString(c.passage.content))) {
    return c.passage;
  }
  const stimulusText = richTextToString(c?.stimulus);
  if (stimulusText.trim()) {
    return { content: stimulusText };
  }
  return null;
};

// Identity key for a passage so we can detect when consecutive questions share
// the same one (explicit `passageId` first, else the passage text itself).
const passageKey = (question?: Question): string | null => {
  if (!question) return null;
  if (question.content?.passageId) return `id:${question.content.passageId}`;
  const passage = getPassage(question);
  if (!passage) return null;
  const text = richTextToString(passage.content);
  if (!text.trim()) return null;
  return `text:${passage.title || ''}::${text}`;
};

interface TestData {
  id: string;
  title: string;
  description: string;
  subject: string;
  difficulty: string;
  total_marks: number;
  estimated_time: number;
  time_limit_minutes: number;
  instructions: string;
  max_attempts: number;
  attempts_used: number;
}

interface TestSession {
  sessionId: string;
  test: TestData;
  questions: Question[];
  timeLimit: number; // in seconds
}

interface TestTakingProps {
  testId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialSession?: any;
  onComplete: (result: any) => void;
  onCancel: () => void;
}

// Readable reading-passage panel. Renders to the left on desktop and above the
// question on mobile (the parent flex container handles the direction). Preserves
// the passage's paragraph/line-break formatting via `whiteSpace: pre-line`.
const HTML_TAG_RE = /<[a-z][\s\S]*>/i;

const PassagePanel: React.FC<{ passage: PassageShape }> = ({ passage }) => {
  // Coerce non-string rich-text objects to string; raw HTML strings survive
  // unchanged because richTextToString returns typeof string values as-is.
  const body =
    typeof passage.content === 'string'
      ? passage.content
      : richTextToString(passage.content);

  const isHtml = HTML_TAG_RE.test(body);
  const attribution = passage.author || passage.source;

  const bodyContainerStyle: React.CSSProperties = {
    fontSize: 16,
    lineHeight: 1.8,
    color: '#374151',
    fontFamily: 'Georgia, "Times New Roman", serif',
    ...(isHtml ? {} : { whiteSpace: 'pre-line' }),
  };

  return (
    <Card
      size="small"
      style={{ background: '#fafafa', height: '100%' }}
      bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
    >
      {passage.title && (
        <Title
          level={5}
          style={{
            fontStyle: 'italic',
            color: '#6b7280',
            textAlign: 'center',
            marginTop: 0,
            marginBottom: attribution ? 4 : 24,
          }}
        >
          {passage.title}
        </Title>
      )}
      {attribution && (
        <div
          style={{
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          {passage.author ? `by ${passage.author}` : passage.source}
        </div>
      )}
      {isHtml ? (
        <div
          style={bodyContainerStyle}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: body }}
        />
      ) : (
        <div style={bodyContainerStyle}>{body}</div>
      )}
    </Card>
  );
};

const TestTaking: React.FC<TestTakingProps> = ({ testId, initialSession, onComplete, onCancel }) => {
  const [testSession, setTestSession] = useState<TestSession | null>((initialSession as TestSession) || null);
  const [loading, setLoading] = useState(!initialSession);
  const [error, setError] = useState<string | null>(null);
  const [paywallError, setPaywallError] = useState<PaywallError | null>(null);
  
  // Test state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // Flag-for-review (exam-day palette, ROO-169).
  const [flagged, setFlagged] = useState<Record<string, boolean>>({});
  // Countdown via the shared hook (ROO-180); setTimeLeft seeds the duration.
  const submitTestRef = useRef<() => void>(() => {});
  const { timeLeft, start: setTimeLeft } = useCountdown(() => submitTestRef.current());
  const [testStartTime, setTestStartTime] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  // "Report a problem" modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportQuestionId, setReportQuestionId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState<string>('wrong_answer');
  const [reportComment, setReportComment] = useState<string>('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const { user: authUser } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  // Get current user data
  const getCurrentUser = () => {
    if (authUser) return authUser;
    const savedUser = localStorage.getItem('cleveroo_user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
      }
    }
    return null;
  };

  const currentUser = getCurrentUser();

  // Start test and load questions.
  // If initialSession was provided by TestRunner, skip the fetch entirely —
  // the session is already in state and time/start have been initialised below.
  const startTest = useCallback(async () => {
    if (initialSession) {
      // Already loaded — just make sure timer and start-time are set.
      setTimeLeft(initialSession.timeLimit);
      setTestStartTime(new Date());
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setPaywallError(null);

      if (!currentUser?.id) {
        throw new Error('User not authenticated');
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/start`,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: false
        }
      );

      if (response.data.success) {
        setTestSession(response.data.data);
        setTimeLeft(response.data.data.timeLimit);
        setTestStartTime(new Date());
        console.log('Test started:', response.data.data);
      } else {
        throw new Error(response.data.error || 'Failed to start test');
      }
    } catch (err: any) {
      const paywall = getPaywallError(err);
      if (paywall) {
        setPaywallError(paywall);
        return;
      }
      console.error('Error starting test:', err);
      setError(err.response?.data?.error || err.message || 'Failed to start test');
    } finally {
      setLoading(false);
    }
  }, [testId, initialSession, currentUser?.id, API_BASE_URL, setTimeLeft]);

  // Submit test
  const submitTest = useCallback(async () => {
    if (!testSession || !currentUser?.id) return;

    try {
      setIsSubmitting(true);

      const endTime = new Date();
      const timeSpent = testStartTime 
        ? Math.floor((endTime.getTime() - testStartTime.getTime()) / 1000)
        : 0;

      const answersArray = testSession.questions.map(question => ({
        questionId: question.id,
        answer: answers[question.id] || '',
        timeSpent: timeSpent / testSession.questions.length
      }));

      const response = await axios.post(
        `${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/submit`,
        {
          sessionId: testSession.sessionId,
          answers: answersArray,
          timeSpent,
          startTime: testStartTime?.toISOString(),
          endTime: endTime.toISOString()
        },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: false
        }
      );

      if (response.data.success) {
        onComplete(response.data.data);
      } else {
        throw new Error(response.data.error || 'Failed to submit test');
      }
    } catch (err: any) {
      console.error('Error submitting test:', err);
      setError(err.response?.data?.error || err.message || 'Failed to submit test');
    } finally {
      setIsSubmitting(false);
    }
  }, [testSession, currentUser?.id, testId, answers, testStartTime, onComplete, API_BASE_URL]);

  // Auto-submit on expiry goes through a ref so the hook always calls the
  // latest submitTest closure (ROO-180).
  useEffect(() => { submitTestRef.current = submitTest; }, [submitTest]);

  // Start test on component mount
  useEffect(() => {
    startTest();
  }, [startTest]);

  // Format time display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // ---- Report a problem ----
  const openReportModal = (questionId: string) => {
    setReportQuestionId(questionId);
    setReportReason('wrong_answer');
    setReportComment('');
    setReportModalOpen(true);
  };

  const closeReportModal = () => {
    if (reportSubmitting) return;
    setReportModalOpen(false);
    setReportQuestionId(null);
  };

  const submitReport = async () => {
    if (!reportQuestionId) return;
    setReportSubmitting(true);
    try {
      const token =
        localStorage.getItem('accessToken') ||
        localStorage.getItem('cleveroo_accessToken') ||
        null;
      await axios.post(
        `${API_BASE_URL}/api/questions/${reportQuestionId}/report`,
        {
          reason: reportReason,
          ...(reportComment.trim() ? { comment: reportComment.trim() } : {}),
        },
        token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : undefined,
      );
      message.success('Thanks! Your report has been sent for review.');
      setReportModalOpen(false);
      setReportQuestionId(null);
    } catch (err) {
      console.error('Failed to submit question report:', err);
      message.error('Could not send your report. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < (testSession?.questions.length || 0) - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleExitTest = () => {
    setShowExitModal(true);
  };

  const confirmExit = () => {
    onCancel();
  };

  if (loading) {
    return (
      <Card style={{ textAlign: 'center', margin: '50px auto', maxWidth: '600px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading test...</Text>
        </div>
      </Card>
    );
  }

  if (paywallError) {
    return <PaywallUpsellCard paywallError={paywallError} onBack={onCancel} />;
  }

  if (error) {
    return (
      <Card style={{ margin: '50px auto', maxWidth: '600px' }}>
        <Result
          status="error"
          title="Failed to Load Test"
          subTitle={error}
          extra={[
            <Button type="primary" onClick={onCancel}>
              Back to Dashboard
            </Button>
          ]}
        />
      </Card>
    );
  }

  if (!testSession || testSession.questions.length === 0) {
    return (
      <Card style={{ margin: '50px auto', maxWidth: '600px' }}>
        <Result
          status="info"
          title="No Questions Available"
          subTitle="This test doesn't have any questions yet."
          extra={[
            <Button type="primary" onClick={onCancel}>
              Back to Dashboard
            </Button>
          ]}
        />
      </Card>
    );
  }

  const currentQuestion = testSession.questions[currentQuestionIndex];
  const currentPassage = getPassage(currentQuestion);
  const hasPassage = !!currentPassage;
  const progress = Math.round(((currentQuestionIndex + 1) / testSession.questions.length) * 100);
  const answeredQuestions = Object.keys(answers).length;
  const isTimeRunningOut = timeLeft <= 300; // 5 minutes

  return (
    <div className="cvr-portal pq-shell">
      {/* Exam-day top bar + progress strip — same atoms as the practice family
          (ROO-179); timer turns red in the final five minutes. */}
      <QuizTopBar
        title={testSession.test.title}
        onCancel={handleExitTest}
        cancelLabel="Exit"
        right={
          <span style={{ color: isTimeRunningOut ? '#B83E24' : undefined, fontWeight: 700 }}>
            <ClockCircleOutlined /> {formatTime(timeLeft)} · Q{currentQuestionIndex + 1}/{testSession.questions.length}
          </span>
        }
      />
      <QuizProgressStrip pct={progress} />

      <div style={{ padding: '20px 16px', maxWidth: hasPassage ? '1200px' : '860px', margin: '0 auto' }}>
      {/* Palette card */}
      <section style={{ background: '#fff', border: '1.5px solid #EFE6D0', borderRadius: 16, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <QuestionPalette
          items={testSession.questions.map((q) => ({ id: q.id, answered: !!answers[q.id], flagged: !!flagged[q.id] }))}
          currentIndex={currentQuestionIndex}
          onJump={setCurrentQuestionIndex}
        />
        <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, color: '#6B7A6F', whiteSpace: 'nowrap' }}>
          Answered {answeredQuestions}/{testSession.questions.length}
        </span>
      </section>

      {/* Passage (when present) + Question.
          On desktop the passage sits to the left and the question to the right;
          the flexWrap collapses them to a single stacked column on narrow
          screens. The passage panel is keyed by its identity so it only
          remounts when the passage actually changes — consecutive questions
          that share a passage keep it mounted (no flicker / scroll reset). */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px',
          alignItems: 'flex-start',
        }}
      >
        {currentPassage && (
          <div style={{ flex: '1 1 360px', minWidth: '280px' }}>
            <PassagePanel key={passageKey(currentQuestion) || 'passage'} passage={currentPassage} />
          </div>
        )}

        <Card style={{ flex: hasPassage ? '1 1 360px' : '1 1 100%', minWidth: '280px', borderRadius: 16, border: '1.5px solid #EFE6D0' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Spelling items skip the title: it's just the truncated sentence,
                which the text renderer below already shows (avoids the doubled
                stem — ROO-212). */}
            {!isTextInputQuestion(currentQuestion as any) && (
              <Title level={5} style={{ marginTop: 0 }}>{currentQuestion.title}</Title>
            )}

            {/* All answer inputs render through the shared type registry
                (ROO-180): one renderer per question type, used by every quiz. */}
            <ExtractsBlock extracts={(currentQuestion.content as any)?.extracts} />
            {renderQuestionInput(
              currentQuestion as any,
              answers[currentQuestion.id] || '',
              (v) => handleAnswerChange(currentQuestion.id, v),
            )}

            <Divider style={{ margin: '8px 0' }} />
            <div style={{ textAlign: 'right' }}>
              <Button
                type="link"
                size="small"
                icon={<FlagOutlined />}
                onClick={() => openReportModal(currentQuestion.id)}
                style={{ color: '#8c8c8c', paddingRight: 0 }}
              >
                Report a problem
              </Button>
            </div>
          </Space>
        </Card>
      </div>

      {/* Navigation — shared shell primitives (ROO-180) */}
      <QuizNavBar
        style={{ marginTop: 20 }}
        left={
          <>
            <QuizBackButton onClick={handlePrevious} disabled={currentQuestionIndex === 0}>← Previous</QuizBackButton>
            {/* Flag-for-review toggles the palette dot (ROO-169) */}
            <QuizBackButton
              onClick={() =>
                setFlagged((prev) => ({ ...prev, [currentQuestion.id]: !prev[currentQuestion.id] }))}
              style={flagged[currentQuestion.id] ? { borderColor: '#E2903A', color: '#9A6215' } : undefined}
            >
              🚩 {flagged[currentQuestion.id] ? 'Unflag' : 'Flag for review'}
            </QuizBackButton>
          </>
        }
        right={
          currentQuestionIndex < testSession.questions.length - 1 ? (
            <QuizPrimaryButton onClick={handleNext}>Next →</QuizPrimaryButton>
          ) : (
            <QuizPrimaryButton onClick={submitTest} disabled={isSubmitting} finish>
              {isSubmitting ? 'Submitting…' : 'Submit Test'}
            </QuizPrimaryButton>
          )
        }
      />

      {/* Exit Confirmation Modal */}
      <Modal
        title="Exit Test"
        open={showExitModal}
        onOk={confirmExit}
        onCancel={() => setShowExitModal(false)}
        okText="Yes, Exit"
        cancelText="Continue Test"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to exit this test? Your progress will be lost.</p>
      </Modal>

      {/* Report a Problem Modal */}
      <Modal
        title="Report a problem"
        open={reportModalOpen}
        onOk={submitReport}
        onCancel={closeReportModal}
        okText="Submit report"
        cancelText="Cancel"
        confirmLoading={reportSubmitting}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            Spotted something wrong with this question? Let us know and we'll review it.
          </Text>
          <Radio.Group
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
          >
            <Space direction="vertical">
              <Radio value="wrong_answer">Wrong answer</Radio>
              <Radio value="typo">Typo</Radio>
              <Radio value="confusing">Confusing wording</Radio>
              <Radio value="off_level">Wrong year level</Radio>
              <Radio value="other">Other</Radio>
            </Space>
          </Radio.Group>
          <Input.TextArea
            value={reportComment}
            onChange={(e) => setReportComment(e.target.value)}
            placeholder="Add any extra detail (optional)"
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={2000}
            showCount
          />
        </Space>
      </Modal>
      </div>
    </div>
  );
};

export default TestTaking;